// src/pages/BiometricManager.js
import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { startRegistration } from '@simplewebauthn/browser';

const webAuthnSupported = () => {
  return window.PublicKeyCredential !== undefined &&
    typeof window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable === 'function';
};

export default function BiometricManager({ staffId }) {
  const [supported, setSupported] = useState(false);
  const [credentials, setCredentials] = useState([]);
  const [registering, setRegistering] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (webAuthnSupported()) {
      window.PublicKeyCredential.isUserVerifyingPlatformAuthenticatorAvailable()
        .then(available => {
          setSupported(available);
          if (available) loadCredentials();
        })
        .catch(() => setSupported(false));
    }
  }, []);

  const loadCredentials = async () => {
    try {
      const res = await api.get('/auth/webauthn/credentials');
      setCredentials(res.data || []);
    } catch { setCredentials([]); }
  };

  const registerBiometric = async () => {
    setRegistering(true);
    setError('');
    setSuccess('');
    try {
      const optionsRes = await api.get('/auth/webauthn/register-options');
      const regResponse = await startRegistration(optionsRes.data);

      await api.post('/auth/webauthn/register', {
        ...regResponse,
        deviceName: getDeviceName(),
      });

      setSuccess('Face ID / Touch ID enabled for this device!');
      loadCredentials();
    } catch (err) {
      if (err.name === 'NotAllowedError') {
        setError('Biometric setup cancelled. Please try again.');
      } else if (err.name === 'InvalidStateError') {
        setError('This device is already registered.');
      } else {
        setError(err.response?.data?.error || 'Failed to set up biometric login. Try again.');
      }
    } finally {
      setRegistering(false);
    }
  };

  const removeCredential = async (credentialID) => {
    if (!window.confirm('Remove biometric login for this device?')) return;
    try {
      await api.delete(`/auth/webauthn/${encodeURIComponent(credentialID)}`);
      setSuccess('Biometric login removed.');
      loadCredentials();
    } catch {
      setError('Failed to remove credential.');
    }
  };

  const getDeviceName = () => {
    const ua = navigator.userAgent;
    if (/iPhone/.test(ua)) return 'iPhone';
    if (/iPad/.test(ua)) return 'iPad';
    if (/Mac/.test(ua)) return 'Mac';
    if (/Android/.test(ua)) return 'Android';
    if (/Windows/.test(ua)) return 'Windows PC';
    return 'This device';
  };

  if (!supported) {
    return (
      <p style={{ fontSize: 13, color: '#9CA3AF' }}>
        Face ID / Touch ID is not available on this device or browser.
      </p>
    );
  }

  return (
    <div>
      {error && <div className="alert-bar danger" style={{ marginBottom: 10 }}>{error}</div>}
      {success && <div className="alert-bar success" style={{ marginBottom: 10 }}>{success}</div>}

      {credentials.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 12, color: '#9CA3AF', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>Registered devices</div>
          {credentials.map(cred => (
            <div key={cred.credentialID} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #F3F4F6' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 500 }}>{cred.deviceName || 'Device'}</div>
                <div style={{ fontSize: 11, color: '#9CA3AF' }}>
                  Registered {new Date(cred.registeredAt).toLocaleDateString('en-US')}
                </div>
              </div>
              <button
                onClick={() => removeCredential(cred.credentialID)}
                style={{ fontSize: 12, color: '#A32D2D', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                Remove
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        onClick={registerBiometric}
        disabled={registering}
        className="btn btn-secondary"
        style={{ fontSize: 13 }}
      >
        {registering ? 'Setting up...' : credentials.length > 0 ? '+ Add another device' : 'Enable Face ID / Touch ID on this device'}
      </button>

      <p style={{ fontSize: 11, color: '#9CA3AF', marginTop: 8, lineHeight: 1.6 }}>
        Biometric data stays on your device and is never sent to our servers.
        Each device must be registered separately.
      </p>
    </div>
  );
}
